import React from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/buttons";
import { OrderType } from "../../../models/checkout";
import { paymentTxt } from "../../../middleware/common-functions";

type OrderInfoProps = {
  order: OrderType;
};

const OrderInfo: React.FC<OrderInfoProps> = ({ order }) => {
  const navigate = useNavigate();

  const handleContinueShopping = () => {
    navigate("/");
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      alert(
        "No se pudo abrir la ventana de impresión. Por favor, permite ventanas emergentes para este sitio."
      );
      return;
    }

    // Get current date and time
    const now = new Date();
    const dateString = now.toLocaleDateString("es-MX");
    const timeString = now.toLocaleTimeString("es-MX");

    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Comprobante de Compra - Mi Cervesa</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .company-info {
            font-size: 10px;
            color: #666;
          }
          .order-info {
            margin-bottom: 20px;
          }
          .section-title {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 10px;
            border-bottom: 1px solid #ccc;
            padding-bottom: 5px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          .info-label {
            font-weight: bold;
          }
          .products-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .products-table th,
          .products-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          .products-table th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .totals-section {
            margin-top: 20px;
            border-top: 2px solid #333;
            padding-top: 15px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          .total-final {
            font-weight: bold;
            font-size: 16px;
            border-top: 1px solid #333;
            padding-top: 10px;
            margin-top: 10px;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 15px;
          }
          .payment-info {
            background-color: #f9f9f9;
            padding: 10px;
            border-radius: 5px;
            margin-bottom: 15px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">Mi Cervesa</div>
          <div class="company-info">
            Comprobante de Compra<br>
            Fecha: ${dateString} - Hora: ${timeString}
          </div>
        </div>

        <div class="order-info">
          <div class="section-title">Información de la Orden</div>
          <div class="info-row">
            <span class="info-label">Estado:</span>
            <span>${order.status || "Pendiente"}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Fecha de Compra:</span>
            <span>${dateString}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Método de Pago:</span>
            <span>${paymentTxt(order.payment_type)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Tipo de Entrega:</span>
            <span>${
              order.delivery_type === "delivery"
                ? "Entrega a Domicilio"
                : order.delivery_type === "pickup"
                ? "Recoger en Tienda"
                : order.delivery_type || "No especificado"
            }</span>
          </div>
        </div>

        ${
          order.address
            ? `
        <div class="order-info">
          <div class="section-title">Información de Envío</div>
          <div class="info-row">
            <span class="info-label">Nombre:</span>
            <span>${order.address.name || ""}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Dirección:</span>
            <span>${order.address.address || ""}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Ciudad:</span>
            <span>${order.address.city || ""}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Estado:</span>
            <span>${order.address.state || ""}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Código Postal:</span>
            <span>${order.address.zip || ""}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Teléfono:</span>
            <span>${order.address.phone || ""}</span>
          </div>
        </div>
        `
            : ""
        }

        <div class="order-info">
          <div class="section-title">Productos</div>
          <table class="products-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Cantidad</th>
                <th>Precio Unit.</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${
                order.products
                  ? order.products
                      .map(
                        (product) => `
                <tr>
                  <td>${product.title || product.descp1 || "Producto"}</td>
                  <td>${product.qty || product.cant || 1}</td>
                  <td>$${product.price_num || product.price || "0.00"}</td>
                  <td>$${(
                    (product.qty || product.cant || 1) *
                    (product.price_num || parseFloat(product.price) || 0)
                  ).toFixed(2)}</td>
                </tr>
              `
                      )
                      .join("")
                  : '<tr><td colspan="4">No hay productos disponibles</td></tr>'
              }
            </tbody>
          </table>
        </div>

        <div class="totals-section">
          <div class="section-title">Resumen de Totales</div>
          ${
            order.totals
              ? `
            <div class="total-row">
              <span>Subtotal:</span>
              <span>$${order.totals.subtotal || "0.00"}</span>
            </div>
            ${
              order.totals.tax
                ? `
            <div class="total-row">
              <span>IVA:</span>
              <span>$${order.totals.tax}</span>
            </div>
            `
                : ""
            }
            ${
              order.totals.shipping
                ? `
            <div class="total-row">
              <span>Envío:</span>
              <span>$${order.totals.shipping}</span>
            </div>
            `
                : ""
            }
            <div class="total-row total-final">
              <span>TOTAL:</span>
              <span>$${order.totals.total || "0.00"}</span>
            </div>
          `
              : '<div class="total-row total-final"><span>TOTAL:</span><span>$0.00</span></div>'
          }
        </div>

        ${
          order.payment_type === "transfer"
            ? `
        <div class="payment-info">
          <div class="section-title">Datos para Transferencia Bancaria</div>
          <div class="info-row">
            <span class="info-label">Banco:</span>
            <span>Banorte</span>
          </div>
          <div class="info-row">
            <span class="info-label">Referencia:</span>
            <span>123456789</span>
          </div>
          <div class="info-row">
            <span class="info-label">Número de Cuenta:</span>
            <span>123456789</span>
          </div>
        </div>
        `
            : ""
        }

        <div class="footer">
          <p>¡Gracias por su compra!</p>
          <p>Para cualquier consulta, contacte a nuestro equipo de atención al cliente.</p>
          <p><strong>Baja California:</strong> +52 (664) 168 49 286 | ventas.tijuana@micervesa.mx</p>
          <p><strong>Resto del país:</strong> +52 (614) 427 86 22 | ventas@micervesa.mx</p>
        </div>
      </body>
      </html>
    `;

    // Write content to the new window
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load, then trigger print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();

      // Close the window after printing (optional)
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };
  return (
    <div className="order-info pt-2 pb-3">
      <Row>
        <Col md={12}>
          <div className="md-semi">Orden de Compra realizada con éxito</div>
          <div className="sm">
            Forma de Pago: {paymentTxt(order.payment_type)}{" "}
          </div>

          <div>
            <p>
              En este momento estamos procesando su pedido, nuestro equipo se
              pondrá en contacto con ustedes una vez esté autorizado
            </p>
          </div>
        </Col>
      </Row>
      <div className="py-3 d-block d-md-flex">
        <div>
          <Button
            onClick={handlePrint}
            children="Imprimir"
            width="180px"
            icon="print"
            title="Imprimir comprobante"
            buttonclasses="gray mr-0 mr-md-5 mb-3 mb-md-3"
          />
        </div>
        <div>
          <Button
            onClick={handleContinueShopping}
            children="Seguir Comprando"
            width="180px"
            icon="shopping-cart"
            title="Continuar comprando"
            buttonclasses="gray-bordered"
          />
        </div>
      </div>
    </div>
  );
};

export default OrderInfo;
